
import axios from 'axios';
import { RoomType, RoomStatus, AssignmentStatus } from '../types/enums';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Room Assignment Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Check if room is stand-alone (no beds allowed)
 */
export const isStandaloneRoom = (room) => {
  if (!room) return false;
  return room.type === RoomType.STANDALONE_ROOM;
};

/**
 * Get room information from facility
 */
export const getRoom = async (roomId, facilityId) => {
  try {
    console.log('Getting room information via API:', roomId, facilityId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/rooms/${roomId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.room;
  } catch (error) {
    console.error('Error getting room:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Room not found in facility');
    }
    throw error;
  }
};

/**
 * Assign patient to room (bed or stand-alone)
 */
export const assignPatientToRoom = async (
  roomId,
  patientId,
  encounterId,
  assignedBy,
  assignedByRole,
  facilityId
) => {
  try {
    console.log('Assigning patient to room via API:', { roomId, patientId, encounterId, facilityId });
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/room-assignments`,
      {
        roomId,
        patientId,
        encounterId,
        assignedBy,
        assignedByRole,
        facilityId,
        encounterType: 'outpatient' // Default for room assignments
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Room assignment created successfully:', response.data.id);
    return {
      assignmentId: response.data.id,
      assignment: response.data
    };
  } catch (error) {
    console.error('Error assigning patient to room:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

/**
 * Strong consistency for stand-alone rooms
 */
export const assignToStandaloneRoom = async (
  roomId,
  patientId,
  encounterId,
  assignedBy,
  assignedByRole,
  room
) => {
  try {
    // Use the general room assignment function - the backend handles the transaction logic
    return await assignPatientToRoom(
      roomId,
      patientId,
      encounterId,
      assignedBy,
      assignedByRole,
      room.facilityId
    );
  } catch (error) {
    console.error('Error assigning to stand-alone room:', error);
    throw error;
  }
};

/**
 * Discharge patient from stand-alone room
 */
export const dischargePatientFromRoom = async (assignmentId, dischargedBy, dischargedByRole, notes = '') => {
  try {
    console.log('Discharging patient from room via API:', assignmentId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/internal/room-assignments/${assignmentId}/discharge`,
      {
        notes,
        dischargedBy,
        dischargedByRole
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Patient discharged from room successfully');
    return {
      assignmentId: response.data.assignmentId,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error discharging patient from room:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

/**
 * Get current room assignment for a patient
 */
export const getCurrentRoomAssignment = async (patientId) => {
  try {
    console.log('Getting current room assignment via API:', patientId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/internal/room-assignments/patient/${patientId}/current`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.assignment;
  } catch (error) {
    console.error('Error getting current room assignment:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Get available stand-alone rooms for a facility
 */
export const getAvailableStandaloneRooms = async (facilityId, filters = {}) => {
  try {
    console.log('Getting available standalone rooms via API:', facilityId, filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/internal/rooms/available?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.availableRooms;
  } catch (error) {
    console.error('Error getting available stand-alone rooms:', error);
    throw error;
  }
};

/**
 * Get all available rooms for a facility (both standalone and rooms with beds)
 */
export const getAllAvailableRooms = async (facilityId, filters = {}) => {
  try {
    console.log('Getting all available rooms via API:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/facility/facilities/${facilityId}/rooms?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Transform to expected format
    const rooms = response.data.rooms || [];
    return rooms.map(room => ({
      roomId: room.code || room.id || room.name,
      name: room.name || room.code || room.id,
      code: room.code || room.id || room.name,
      status: RoomStatus.AVAILABLE, // Default to available
      capacity: room.capacity || 1,
      roomType: room.type || 'Room',
      departmentId: room.departmentId,
      departmentName: room.departmentName || 'Unknown',
      type: room.type || 'Room'
    }));
  } catch (error) {
    console.error('Error getting all available rooms:', error);
    throw error;
  }
};

/**
 * Transfer patient between stand-alone rooms
 */
export const transferPatientBetweenRooms = async (
  currentAssignmentId,
  newRoomId,
  transferredBy,
  transferredByRole,
  notes = ''
) => {
  try {
    console.log('Transferring patient between rooms via API:', { currentAssignmentId, newRoomId });
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/room-assignments/${currentAssignmentId}/transfer`,
      {
        newRoomId,
        notes,
        transferredBy,
        transferredByRole
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Patient transferred between rooms successfully');
    return {
      oldAssignmentId: response.data.previousAssignmentId,
      newAssignmentId: response.data.newAssignmentId,
      newAssignment: response.data
    };
  } catch (error) {
    console.error('Error transferring patient between rooms:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

/**
 * Get room assignments with filtering
 */
export const getRoomAssignments = async (filters = {}, page = 1, limit = 50) => {
  try {
    console.log('Getting room assignments via API with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/internal/room-assignments?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      roomAssignments: response.data.roomAssignments,
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error getting room assignments:', error);
    throw error;
  }
};

/**
 * Initialize room status for stand-alone rooms
 */
export const initializeRoomStatus = async (facilityId, departments) => {
  try {
    console.log('Initializing room status via API:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/internal/rooms/initialize-status`,
      {
        facilityId,
        departments
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Room status initialized successfully:', response.data.totalRooms);
    return response.data;
  } catch (error) {
    console.error('Error initializing room status:', error);
    throw error;
  }
};

/**
 * Get room assignments for a specific facility
 */
export const getRoomAssignmentsForFacility = async (facilityId, filters = {}, page = 1, limit = 50) => {
  return await getRoomAssignments({ facilityId, ...filters }, page, limit);
};

/**
 * Get active room assignments for a facility
 */
export const getActiveRoomAssignments = async (facilityId, page = 1, limit = 50) => {
  return await getRoomAssignments({ facilityId, status: AssignmentStatus.ACTIVE }, page, limit);
};

/**
 * Get room assignments by department
 */
export const getRoomAssignmentsByDepartment = async (facilityId, departmentId, page = 1, limit = 50) => {
  return await getRoomAssignments({ facilityId, departmentId }, page, limit);
};

/**
 * Check if a room is available
 */
export const isRoomAvailable = async (roomId, facilityId) => {
  try {
    const assignments = await getRoomAssignments({ roomId, facilityId, status: AssignmentStatus.ACTIVE }, 1, 1);
    return assignments.roomAssignments.length === 0;
  } catch (error) {
    console.error('Error checking room availability:', error);
    return false;
  }
};

/**
 * Get room occupancy statistics for a facility
 */
export const getRoomOccupancyStats = async (facilityId) => {
  try {
    const [totalAssignments, activeAssignments] = await Promise.all([
      getRoomAssignments({ facilityId }, 1, 1000), // Get total count
      getRoomAssignments({ facilityId, status: AssignmentStatus.ACTIVE }, 1, 1000)
    ]);
    
    return {
      totalRooms: totalAssignments.roomAssignments.length,
      occupiedRooms: activeAssignments.roomAssignments.length,
      availableRooms: totalAssignments.roomAssignments.length - activeAssignments.roomAssignments.length,
      occupancyRate: totalAssignments.roomAssignments.length > 0 
        ? (activeAssignments.roomAssignments.length / totalAssignments.roomAssignments.length * 100).toFixed(1)
        : 0
    };
  } catch (error) {
    console.error('Error getting room occupancy stats:', error);
    return {
      totalRooms: 0,
      occupiedRooms: 0,
      availableRooms: 0,
      occupancyRate: 0
    };
  }
};

/**
 * Update encounter location information (handled by backend)
 */
export const updateEncounterLocationForRoom = async (encounterId, roomInfo) => {
  try {
    if (!encounterId) return;
    
    console.log('[roomAssignmentService] Updating encounter location for encounterId:', encounterId);
    const token = await getAuthToken();
    
    // The backend API typically handles this automatically during room assignment
    // but we can provide an explicit endpoint if needed
    const updateData = {
      room: roomInfo.name || roomInfo.code,
      roomId: roomInfo.code || roomInfo.id,
      bed: null, // Clear bed info for room-only assignment
      bedId: null
    };
    
    const response = await axios.put(
      `${API_BASE}/clinical/encounters/${encounterId}/location`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log(`Updated encounter ${encounterId} with new room:`, updateData.room);
    return response.data;
  } catch (error) {
    console.error('Error updating encounter location for room:', error);
    // Don't throw - this is a supplementary operation
  }
};
